import { computed, inject, Injectable } from '@angular/core';
import { SettingsStore } from '@settings/data-access';
import * as currency from 'locale-currency';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class FormattingService {
  private readonly defaultCulture = 'pt-BR'
  private readonly settingsStore = inject(SettingsStore);
  private readonly ownerSettings = computed(() => this.settingsStore.ownerSettings());
  private readonly culture = computed(() => this.ownerSettings()?.Culture ?? this.defaultCulture);

  public normalizeNumber(value: string | number): number {
    if (!value && value !== 0) return 0;

    const numericValue = typeof value === 'string'
      ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.'))
      : value;

    if (isNaN(numericValue)) return 0;

    return numericValue;
  }

  public formatCurrency(value: number | string, locale?: string) {
    const numericValue = this.normalizeNumber(value);
    const cultureToUse = locale ?? this.culture();
    const currencyCode = currency.getCurrency(cultureToUse) ?? 'XXX';
    
    return new Intl.NumberFormat(cultureToUse, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol'
    }).format(numericValue);
  }

  public formatToPartsCurrency(value: number | string, locale?: string) {
    const numericValue = this.normalizeNumber(value);
    const cultureToUse = locale ?? this.culture();
    const currencyCode = currency.getCurrency(cultureToUse) ?? 'XXX';
    
    return new Intl.NumberFormat(cultureToUse, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol'
    }).formatToParts(numericValue);
  }

  public formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions, locale?: string) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    const isUTCZero = typeof date === 'string' && 
      (date.trim().endsWith('Z') || date.includes('+00:00') || date.includes('T00:00:00'));

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(isUTCZero && { timeZone: 'UTC' })
    };

    const cultureToUse = locale ?? this.culture();
    const result = dateObj.toLocaleDateString(cultureToUse, { ...defaultOptions, ...options });
    return result;
  }

  public formatTime(time: Date | string, options?: Intl.DateTimeFormatOptions, locale?: string) {
    let timeObj: Date;

    if (typeof time === 'string') {
      const trimmedTime = time.trim();
      if (!trimmedTime) return '';
      const timePattern = /^(\d{1,2}):(\d{2})$/;
      const match = trimmedTime.match(timePattern);

      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);

        if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
          timeObj = new Date();
          timeObj.setHours(hours, minutes, 0, 0);
        } else {
          return '';
        }
      } else {
        timeObj = new Date(trimmedTime);
        if (isNaN(timeObj.getTime())) return '';
      }
    } else {
      timeObj = time;
    }

    if (isNaN(timeObj.getTime())) return '';

    const defaultOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit'
    };

    const cultureToUse = locale ?? this.culture();
    return timeObj.toLocaleTimeString(cultureToUse, { ...defaultOptions, ...options });
  }

  public formatDateTime(dateTime: Date | string, options?: Intl.DateTimeFormatOptions) {
    const dateTimeObj = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
    if (isNaN(dateTimeObj.getTime())) return '';

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    };

    return dateTimeObj.toLocaleString(this.culture(), { ...defaultOptions, ...options });
  }

  public formatNumber(value: number | string, decimals?: number, locale?: string, options?: Intl.NumberFormatOptions) {
    const numericValue = this.normalizeNumber(value);
    const cultureToUse = locale ?? this.culture();

    return new Intl.NumberFormat(cultureToUse, options || {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(numericValue);
  }

  public getCurrencySymbol(): string {
    const currencyCode = currency.getCurrency(this.culture());
    const formatter = new Intl.NumberFormat(this.culture(), {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const parts = formatter.formatToParts(0);
    const currencySymbol = parts.find((part) => part.type === 'currency')?.value ?? '';
    return currencySymbol;
  }

  public getSeparators(): { thousandSeparator: string; decimalSeparator: string } {
    const numberFormatter = new Intl.NumberFormat(this.culture());
    const parts = numberFormatter.formatToParts(1234567.89);
    const thousandSeparator = parts.find((part) => part.type === 'group')?.value ?? ',';
    const decimalSeparator = parts.find((part) => part.type === 'decimal')?.value ?? '.';
    return { thousandSeparator, decimalSeparator };
  }

  public getDateMask(): string {
    const formatter = new Intl.DateTimeFormat(this.culture(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(new Date());

    return parts.map(part => {
      if (part.type === 'day' || part.type === 'month') return '00';
      if (part.type === 'year') return '0000';
      return part.value;
    }).join('') ?? '00/00/0000';
  }

  public getDateFormatPattern(): string {
    const formatter = new Intl.DateTimeFormat(this.culture(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(new Date());

    return parts.map(part => {
      if (part.type === 'day') return 'dd';
      if (part.type === 'month') return 'MM';
      if (part.type === 'year') return 'yyyy';
      return part.value;
    }).join('') ?? 'dd/MM/yyyy';
  }

  public getTimeMask(): string {
    const formatter = new Intl.DateTimeFormat(this.culture(), {
      hour: '2-digit',
      minute: '2-digit'
    });
    const parts = formatter.formatToParts(new Date());

    return parts.map(part => {
      if (part.type === 'hour' || part.type === 'minute') return '00';
      return part.value;
    }).join('') ?? '00:00';
  }

  public getTimeFormatPattern(): string {
    const formatter = new Intl.DateTimeFormat(this.culture(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const parts = formatter.formatToParts(new Date());

    return parts.map(part => {
      if (part.type === 'hour') return 'HH';
      if (part.type === 'minute') return 'mm';
      return part.value;
    }).join('') ?? 'HH:mm';
  }

  public getDateTimeMask(): string {
    return this.getDateMask() + ' ' + this.getTimeMask();
  }

  public getDateTimeFormatPattern(): string {
    return this.getDateFormatPattern() + ' ' + this.getTimeFormatPattern();
  }
}
